import { useCallback, useContext, useMemo, useRef } from 'react';
import Promise from 'bluebird';
import { extend } from 'underscore';
import { useWizardService } from '@bingads-webui-react/wizard';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import {
  readCampaignNegativeWebpages,
  readDayTimeCriterions,
  readLocationCriterions,
  readNewCustomerAcquisitionGoals,
} from '@bingads-webui-campaign-react/perf-max-data';
import {
  loadCampaign,
  modelToViewModelForCampaign,
  loadBundleCountries,
  loadAssetGroupWithVideos,
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { createVideoRepository } from '@bingads-webui-campaign-react/asset-library-services';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import Repository from '@bingads-webui-campaign-legacy/adextension-shared/service/repository';
import { DisclaimerAdExtension } from '@bingads-webui-campaign/campaign-ui-schemas';
import {
  getAssociatedDisclaimer,
} from '@bingads-webui-campaign-react/display-plus-campaigns';
import { PerformanceMarkerContext } from '@bingads-webui-campaign-react/performance-marker-provider';
import { updateAssetGroupAndCampaignStatus } from '../../shared/model/update-entity-status';
import { useDiagnosticsData } from './diagnostics/use-diagnostics-data';
import { convertDiagnosticsDataToTrackingLog } from './diagnostics/utils';
import { getODataConfig } from '../../shared/components/odata-config';

export const useSummaryStepRepository = () => {
  const { state } = useWizardService();

  const { scope } = useContext(ScopeContext) || {};

  const serverContext = useContext(ServerContext);
  const activity = useContext(ActivityContext);
  const isSearchThemeEnabled = serverContext.DynamicPermissions.SearchThemes;
  const isUrlRulesEnabled = serverContext.DynamicPermissions.AssetGroupUrlRules;
  const isPMaxCreationDiagnosticEnabled = serverContext.DynamicPermissions.PMaxCreationDiagnostic;
  const isBudgetSuggestionEnabled = serverContext.DynamicPermissions.EnableBudgetSuggestion;
  const isNewCustomerAcquisitionEnabled = serverContext.DynamicPermissions.NewCustomerAcquisition;
  const { AdvertiserCustomerID: advertiserCustomerId } = serverContext.CurrentAccount || {};
  const isPMaxDisclaimersEnabled = !serverContext.Permissions.IsDomesticSimplifiedChineseEnabled && serverContext.DynamicPermissions.PMaxDisclaimers && serverContext.Permissions.IsMSANDisclaimerEnabled;

  const i18n = useContext(I18nContext);

  const oDataConfig = useMemo(() => getODataConfig(serverContext), [serverContext]);
  const { cid, aid } = scope.values;

  const videoRepository = createVideoRepository({
    oDataConfig,
    aid,
    cid,
  });

  const disclaimerRepositoryRef = useRef(new Repository({
    schema: DisclaimerAdExtension,
    skipExtensionCache: true,
  }));

  const perfMarker = useContext(PerformanceMarkerContext);
  const disclaimerPerfMarker = perfMarker.createChild('Pmax_Disclaimers_Summary_Step_Load_Perf_Marker');

  const {
    campaign: savedCampaign,
    assetGroup: savedAssetGroup,
    diagnostics: savedDiagnostics,
    isPublishing,
  } = state.context.wizardData;

  const { fetchDiagnosticData, patchDiagnosticsData } = useDiagnosticsData({
    advertiserCustomerId,
    aid,
    campaignId: savedCampaign.Id,
    serverContext,
  });

  const load = useCallback(() => {
    const { Id: campaignId } = savedCampaign || {};

    const { Id: assetGroupId } = savedAssetGroup || {};

    const loadPromises = {
      campaign: loadCampaign(cid, aid, campaignId),
      locationTargets: readLocationCriterions(cid, aid, campaignId),
      bundleCountriesAndLocationCriterions: loadBundleCountries({
        cid,
        aid,
        serverContext,
      }),
      dayTimeCriterions: readDayTimeCriterions(cid, aid, campaignId),
      campaignNegativeWebpages: readCampaignNegativeWebpages(cid, aid, campaignId),
      assetGroup: loadAssetGroupWithVideos(cid, aid, campaignId, assetGroupId, i18n, isSearchThemeEnabled, isUrlRulesEnabled, videoRepository),
      ...(isPMaxCreationDiagnosticEnabled && { diagnostics: fetchDiagnosticData() }),
      ...(isNewCustomerAcquisitionEnabled && { newCustomerAcquisitionGoals: readNewCustomerAcquisitionGoals(cid, aid) }),
      ...(isPMaxDisclaimersEnabled && {
        disclaimer: getAssociatedDisclaimer(campaignId, disclaimerRepositoryRef.current, disclaimerPerfMarker),
      }),
    };

    return Promise.props(loadPromises).then(({
      campaign,
      locationTargets,
      bundleCountriesAndLocationCriterions,
      dayTimeCriterions,
      campaignNegativeWebpages,
      assetGroup,
      diagnostics,
      newCustomerAcquisitionGoals,
      disclaimer,
    }) => {
      const campaignData = modelToViewModelForCampaign({
        campaignModel: campaign,
        locationCriterionsModel: locationTargets ? locationTargets.value : [],
        dayTimeCriterionsModel: dayTimeCriterions ? dayTimeCriterions.value : [],
        campaignNegativeWebpagesModel: campaignNegativeWebpages ? campaignNegativeWebpages.value : [],
        bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
        ...(isNewCustomerAcquisitionEnabled && { newCustomerAcquisitionGoalsModel: newCustomerAcquisitionGoals ? newCustomerAcquisitionGoals.value : [] }),
      });

      let data = {
        campaign: campaignData.data,
        assetGroup: assetGroup.data,
      };

      if (isPMaxCreationDiagnosticEnabled && diagnostics) {
        const budgetSuggestionData = isBudgetSuggestionEnabled ? savedDiagnostics.budgetSuggestionData : null;
        data = extend(data, {
          diagnosticsData: patchDiagnosticsData({
            diagnosticsData: diagnostics,
            campaignData: campaignData.data,
            budgetSuggestionData,
          }),
        });
        if (data.diagnosticsData.length > 0) {
          setClarityTag({ isPMaxDiagnostic: true });
        }
        activity.trace(`[PmaxNorthStarWizard][SummaryStep][Checks] ${convertDiagnosticsDataToTrackingLog(data.diagnosticsData)}`);
      }

      if (isPMaxDisclaimersEnabled && disclaimer) {
        data = extend(data, {
          disclaimer: {
            Name: disclaimer.Name,
          },
        });
      }

      return data;
    }).catch((errorData) => {
      activity.trace(`[PmaxNorthStarWizard][SummaryStep] loadData failed. Error: ${errorData}`);

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        error: {
          ...errorData,
        },
      });
    });
  }, [activity, aid, cid, fetchDiagnosticData, i18n, isBudgetSuggestionEnabled, isNewCustomerAcquisitionEnabled, isPMaxCreationDiagnosticEnabled, isSearchThemeEnabled, isUrlRulesEnabled, patchDiagnosticsData, savedAssetGroup, savedCampaign, savedDiagnostics.budgetSuggestionData, serverContext, isPMaxDisclaimersEnabled]);

  const save = useCallback(({ data }) => {
    const { Id: campaignId } = savedCampaign || {};
    const { Id: assetGroupId } = savedAssetGroup || {};

    if (isPublishing) {
      return updateAssetGroupAndCampaignStatus({ campaignId, assetGroupId }).then(() => ({
        campaign: data.campaign,
        assetGroup: data.assetGroup,
      })).catch(errorData => (
        // eslint-disable-next-line prefer-promise-reject-errors
        Promise.reject({ error: { ...errorData } })));
    }

    // no update necessary if not publishing.
    return Promise.resolve({
      campaign: data.campaign,
      assetGroup: data.assetGroup,
    });
  }, [isPublishing, savedAssetGroup, savedCampaign]);

  return {
    load,
    save,
  };
};
